import { getSocketAuth } from "@/lib/session";
import type { PaymentContextValue, PaymentLog } from "@/types/payment";
import { usePaymentBase } from "@platko/shared/usePaymentBase";
import { buildRestoredPaymentState, connectPaymentSocket, restoreProcessedInvoiceIds } from "@platko/shared/utils";
import api from "@shared/api/client";
import { PaymentFlowStatus, PaymentSource, PaymentStatus } from "@shared/types/payment";
import type { SocketHandle } from "@shared/types/socket";
import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";

const PaymentContext = createContext<PaymentContextValue>(null!);

const PaymentProvider = ({ children }: { children: React.ReactNode }) => {
  const base = usePaymentBase();
  const socketHandleRef = useRef<SocketHandle | null>(null);
  const [showPending, setShowPending] = useState(false);
  const [selectedCount, setSelectedCount] = useState(0);
  const [showLogs, setShowLogs] = useState(false);
  const [logsStarted, setLogsStarted] = useState(false);
  const logPanelRef = useRef<HTMLDivElement | null>(null);

  const connectToPayment = useCallback(
    async (paymentLogId: number) => {
      socketHandleRef.current?.disconnect();
      const auth = await getSocketAuth();
      socketHandleRef.current = connectPaymentSocket(
        paymentLogId,
        {
          onMessage: (event) => {
            base.processPaymentEvent(event);
            if (event.type === "error") setSelectedCount(0);
          },
          onError: (message) => {
            base.patch({ paymentStatus: PaymentFlowStatus.ERROR, payError: message, twoFaPrompt: null });
          },
        },
        auth,
      );
    },
    [base.processPaymentEvent, base.patch],
  );

  useEffect(() => {
    base.initPaymentHandlers({
      onSocketDisconnect: () => socketHandleRef.current?.disconnect(),
      onConnectToPayment: connectToPayment,
      onLogReload: () => void base.loadPaymentLogs(false),
    });
  }, [connectToPayment, base.initPaymentHandlers, base.loadPaymentLogs]);

  const executePayment = useCallback(
    async (invoiceIds: number[]) => {
      setSelectedCount(invoiceIds.length);
      await base.executePayment(invoiceIds, PaymentSource.FE);
    },
    [base.executePayment],
  );

  const handleCancelPayment = useCallback(async () => {
    await base.cancelPayment("Payment cancelled");
  }, [base.cancelPayment]);

  const handleLoadPending = useCallback(async () => {
    try {
      const data = await api.invoices.pending();
      base.patch({ pendingInvoices: data });
      setShowPending(true);
    } catch (err: unknown) {
      console.error("Failed to load pending invoices:", err instanceof Error ? err.message : String(err));
    }
  }, [base.patch]);

  useEffect(() => {
    void base.loadPaymentLogs(false);
    return () => socketHandleRef.current?.disconnect();
  }, [base.loadPaymentLogs]);

  useEffect(() => {
    handleLoadPending();
  }, [handleLoadPending]);

  const hasCheckedRunningRef = useRef(false);

  const restorePaymentState = useCallback(
    (data: PaymentLog | null) => {
      if (!data || data.status !== PaymentStatus.RUNNING) return;
      const { logEntries, twoFaPrompt } = buildRestoredPaymentState(data);
      restoreProcessedInvoiceIds(base.processedInvoiceIds.current, data.entries);
      base.patch({
        paying: true,
        paymentStatus: PaymentFlowStatus.PROCESSING,
        currentPaymentLogId: data.id,
        logEntries,
        twoFaPrompt,
        payResults: data.entries ?? [],
        processedCount: data.totalInvoices,
      });
      setShowPending(true);
      setSelectedCount(data.totalInvoices);
      void connectToPayment(data.id);
    },
    [connectToPayment, base.patch, base.processedInvoiceIds],
  );

  useEffect(() => {
    if (hasCheckedRunningRef.current) return;
    hasCheckedRunningRef.current = true;

    void api.paymentLogs
      .getRunning()
      .then(({ data }) => restorePaymentState(data))
      .catch((err: unknown) => {
        const msg = err instanceof Error ? err.message : String(err);
        console.error("Failed to check running payment:", msg);
      });
  }, [restorePaymentState]);

  const contextValue: PaymentContextValue = {
    ...base.state,
    patch: base.patch,
    showPending,
    setShowPending,
    selectedCount,
    setSelectedCount,
    showLogs,
    setShowLogs,
    logsStarted,
    setLogsStarted,
    logPanelRef,
    socketHandleRef,
    loadPaymentLogs: () => void base.loadPaymentLogs(false),
    executePayment,
    handleCancelPayment,
    handleLoadPending,
    handleSubmit2Fa: base.submit2Fa,
    handleCancel2Fa: base.cancel2Fa,
  };

  return <PaymentContext.Provider value={contextValue}>{children}</PaymentContext.Provider>;
};

const usePayment = (): PaymentContextValue => useContext(PaymentContext);

export { PaymentProvider, usePayment };
